// Ticketbezogene Zeiten (Etappe 3) — reine Regeln.
//
// Abgrenzung zur Arbeitszeit: `zeit_stempel` sagt, WANN jemand gearbeitet hat (Grundlage
// für Stundenkonto und Lohn), `ticket_zeiten` sagt WOFÜR. Beide Werte nebeneinander zu
// stellen ist erwünscht (siehe `abdeckung`), addieren darf man sie nicht.
import { dauerMinuten, stundenText, tagVon } from './berechnung';

export interface TicketZeit {
  id: string;
  ticket_id: string;
  mitarbeiter_id: string;
  beginn: string;
  ende: string | null;
  taetigkeit: string | null;
  abrechenbar: boolean;
  objekt_id: string | null;
  quelle?: string | null;
  created_at?: string;
}

/** Minuten einer Buchung; laufende zählen gegen `jetzt`. */
export function zeitMinuten(z: TicketZeit, jetzt: Date = new Date()): number {
  return dauerMinuten(z.beginn, z.ende, jetzt);
}

export function summeMinuten(zeiten: TicketZeit[], jetzt: Date = new Date()): number {
  return zeiten.reduce((s, z) => s + zeitMinuten(z, jetzt), 0);
}

export interface Aufteilung {
  gesamt: number;
  abrechenbar: number;
  intern: number;
}

/** Summe getrennt nach abrechenbar und intern. */
export function aufteilung(zeiten: TicketZeit[], jetzt: Date = new Date()): Aufteilung {
  const abrechenbar = summeMinuten(zeiten.filter((z) => z.abrechenbar), jetzt);
  const gesamt = summeMinuten(zeiten, jetzt);
  return { gesamt, abrechenbar, intern: gesamt - abrechenbar };
}

/** Gruppiert nach einem Schlüssel (Ticket, Objekt, Mitarbeiter) — größte Summe zuerst. */
export function gruppiere<T extends string>(
  zeiten: TicketZeit[],
  schluessel: (z: TicketZeit) => T | null,
  jetzt: Date = new Date(),
): { key: T; minuten: number; abrechenbar: number; anzahl: number }[] {
  const map = new Map<T, { minuten: number; abrechenbar: number; anzahl: number }>();
  for (const z of zeiten) {
    const k = schluessel(z);
    if (k === null) continue;
    const eintrag = map.get(k) || { minuten: 0, abrechenbar: 0, anzahl: 0 };
    const m = zeitMinuten(z, jetzt);
    eintrag.minuten += m;
    if (z.abrechenbar) eintrag.abrechenbar += m;
    eintrag.anzahl += 1;
    map.set(k, eintrag);
  }
  return [...map.entries()]
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => b.minuten - a.minuten);
}

/** Buchungen eines Zeitraums (Tagesgrenzen einschließlich). */
export function imZeitraum(zeiten: TicketZeit[], vonTag: string, bisTag: string): TicketZeit[] {
  return zeiten.filter((z) => {
    const t = tagVon(z.beginn);
    return t >= vonTag && t <= bisTag;
  });
}

/**
 * Wie viel der erfassten Arbeitszeit ist einem Ticket zugeordnet? 0–100 %.
 * Über 100 % bedeutet: mehr auf Tickets gebucht als überhaupt gestempelt — ein
 * Hinweis auf doppelte Buchungen, keine Beanstandung.
 */
export function abdeckung(ticketMinuten: number, arbeitsMinuten: number): number | null {
  if (arbeitsMinuten <= 0) return null;
  return Math.round((ticketMinuten / arbeitsMinuten) * 100);
}

/** Ein Satz für den Ticket-Verlauf: „1:30 h · Heizung entlüftet · abrechenbar". */
export function protokollZeile(z: TicketZeit, jetzt: Date = new Date()): string {
  const teile = [stundenText(zeitMinuten(z, jetzt))];
  if (z.taetigkeit?.trim()) teile.push(z.taetigkeit.trim());
  teile.push(z.abrechenbar ? 'abrechenbar' : 'intern');
  return teile.join(' · ');
}

/** Prüft eine manuelle Nachbuchung; gibt den Grund zurück oder null. */
export function buchungFehler(a: { tag: string; von: string; bis: string }): string | null {
  if (!a.tag || !a.von || !a.bis) return 'Bitte Tag, Von und Bis angeben.';
  const beginn = new Date(`${a.tag}T${a.von}:00`);
  const ende = new Date(`${a.tag}T${a.bis}:00`);
  if (Number.isNaN(beginn.getTime()) || Number.isNaN(ende.getTime())) return 'Bitte Datum und Uhrzeiten prüfen.';
  if (ende <= beginn) return 'Das Ende muss nach dem Beginn liegen.';
  if ((ende.getTime() - beginn.getTime()) / 3600000 > 24) return 'Mehr als 24 Stunden am Stück sind nicht möglich.';
  return null;
}
